import { IconEye } from "@tabler/icons-react";
import React from "react";

type Column<T> = {
  label: string | React.ReactNode;
  render?: (item: T, index: number) => React.ReactNode;
  className?: string;
  children?: Column<T>[]; // 👈 allow sub-columns
};

interface ListViewProps<T> {
  data: T[];
  columns: Column<T>[];
  multiData?: T[];
  onRowClick?: (item: T) => void;
  mainDivClassName?: string;
  mailDivClassName?: string;
  trClassName?: string;
  tdClassName?: string;
  thClassName?: string;
  tbodyClassName?: string;
  tableType?: string;
  ActionClassName?: string;
  TableClassName?: string;
  renderExpandedContent?: (data: T) => React.ReactNode; // 👈 Optional expanded content
  expandedRows?: Set<number>; // 👈 Track expanded rows
  scrollbarClassName?: string;
  splitToggle?: boolean;
}

function ListView<T>({
  data,
  columns,
  onRowClick,
  TableClassName,
  tableType,
  mailDivClassName = "p-0 rounded-b-3xl rounded-t-none bg-transparent",
  mainDivClassName = "bg-transparent",
  trClassName = "hover:bg-[#FBD037]/10 group transition-colors duration-150 cursor-pointer",
  thClassName = "bg-transparent",
  tdClassName = "",
  tbodyClassName = "bg-transparent",
  ActionClassName,
  splitToggle,
  multiData,
  renderExpandedContent,
  expandedRows,
  scrollbarClassName = "hide-scrollbar",
}: ListViewProps<T>) {
  const hasGroups = columns.some((c) => c.children && c.children.length > 0);
  const hasExpandedContent = !!renderExpandedContent;

  return (
    <div className={`${mainDivClassName}`}>
      <div className={`overflow-x-auto overflow-y-auto max-h-[90vh] ${scrollbarClassName} ${mailDivClassName}`}>
        <table className="w-full text-sm whitespace-nowrap min-w-max">
          <thead
            className={`text-[#131327] font-bold border-b border-t border-dashed border-gray-300 sticky top-0 z-20 shadow-sm ${thClassName}`}
          >
            {/* Top row */}
            <tr>
              {columns.map((col, i) => {
                const isGroup = col.children && col.children.length > 0;

                return (
                  <th
                    key={i}
                    colSpan={isGroup ? col.children!.length : 1}
                    rowSpan={isGroup ? 1 : hasGroups ? 2 : 1}
                    className={`font-semibold px-4 py-2.5 border-r last:border-none border-dashed border-gray-200 select-none
                      ${isGroup ? "text-left border-b border-dashed" : "text-left"} 
                    `}
                  >
                    {col.label}
                  </th>
                );
              })}
            </tr>

            {/* Second row for grouped subheaders */}
            {hasGroups && (
              <tr>
                {columns.flatMap((col, i) =>
                  col.children
                    ? col.children.map((child, j) => (
                        <th
                          key={`${i}-${j}`}
                          className="text-left font-normal px-4 py-1.5 border-r border-dashed border-gray-200 text-[#131327]/70"
                        >
                          {child.label}
                        </th>
                      ))
                    : []
                )}
              </tr>
            )}
          </thead>

          <tbody className={`divide-y divide-gray-100 ${tbodyClassName}`}>
            {data?.map((item, idx) => {
              const key = (item as any)?.id?.toString?.() ?? idx.toString();
              const idNum = parseInt(key, 10);
              const isExpanded = hasExpandedContent && expandedRows?.has(idNum);

              return (
                <React.Fragment key={key}>
                  {/* Main row */}
                  <tr
                    onClick={() => onRowClick?.(item)}
                    className={`${onRowClick ? "cursor-pointer" : "cursor-default"} ${trClassName} ${
                      isExpanded ? "bg-gray-50" : ""
                    }`}
                  >
                    {columns.flatMap((col, i) => {
                      if (col.children && col.children.length > 0) {
                        return col.children.map((child, j) => (
                          <td
                            key={`${i}-${j}`}
                            className={`px-4 py-2.5 text-[#212121] text-xs text-left border-r last:border-none border-dashed border-gray-200 ${tdClassName}`}
                          >
                            {child.render ? child.render(item, idx) : null}
                          </td>
                        ));
                      }
                      return (
                        <td
                          key={i}
                          className={`px-4 py-2.5 text-[#212121] text-left text-xs border-r last:border-none border-dashed border-gray-200 ${tdClassName}`}
                        >
                          {col.render ? col.render(item, idx) : null}
                        </td>
                      );
                    })}
                  </tr>

                  {/* Expanded content row */}
                  {isExpanded && renderExpandedContent && (
                    <tr>
                      <td
                        colSpan={
                          (hasExpandedContent ? 1 : 0) +
                          columns.reduce(
                            (total, col) =>
                              total + (col.children ? col.children.length : 1),
                            0
                          )
                        }
                        className="p-0 border-b border-gray-200"
                      >
                        {renderExpandedContent(item)}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}

            {splitToggle && (
              <>
                {multiData?.map((item, idx) => (
                  <tr className="bg-white border-t border-white ">
                    <td className=" text-[#212121] h-[67px]">
                      <div className="flex justify-center items-center h-full">
                        <div className="w-px h-16 bg-gray-300"></div>
                        <div className="w-3/6 h-px bg-gray-300"></div>
                      </div>
                    </td>
                    <td className="px-6 py-3 text-[#212121] text-xs">#04564665-1</td>
                    <td className="px-6 py-3 text-[#212121] text-xs">SKU123</td>
                    <td className="px-6 py-3 text-[#212121] text-xs">Alok Kumar</td>
                    <td className="px-6 py-3 text-[#212121] text-xs">25</td>

                    <td className="px-6 py-3 text-[#212121] text-xs"> Self</td>
                    <td className="px-6 py-3 text-[#212121] text-xs">
                      4 Aug 2025, 6:40 AM
                    </td>
                    <td className="px-6 py-3 text-[#212121] text-xs">
                      <div className="flex justify-center">
                        {true && (
                          <span className="px-4 py-1.5 text-xs font-medium rounded-full bg-[#FF4848]/20 border border-[#FF4848]">
                            Pending
                          </span>
                        )}
                        {false && (
                          <span className="px-4 py-1.5 text-xs font-medium rounded-full bg-[#20BF55]/20 border border-[#20BF55]">
                            Completed
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="text-center px-6 py-3 text-[#212121] text-xs">
                      <div className="text-center">
                        <button className="p-2 border rounded-full bg-[#F7F6F2]">
                          <IconEye size={22} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default ListView;
